import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  Download,
  RotateCcw,
} from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableToolbar } from "../components/TableToolbar";
import { FilterCard } from "../components/FilterCard";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { getStoredAccountType } from "../auth";
import {
  admissionCompanyFilterOptions,
  admissionHospitalFilterOptions,
  admissionPatientTypeOptions,
  admissions as mockAdmissions,
} from "../data";
import { useAuditorAdmissions } from "../hooks/useAuditorAdmissions";
import type { Admission } from "../types";
import { adminDashboardSummary } from "../data/adminDashboard";

function parseAdmissionDate(date: string, time = "00:00"): Date {
  const [day, month, year] = date.split("/").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  return new Date(year, month - 1, day, hours, minutes);
}

function parseInputDate(value: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function getDashboardHomeHref() {
  const accountType = getStoredAccountType();
  if (accountType === "auditor") return "/dashboard/auditor";
  if (accountType === "hospital") return "/dashboard/hospital";
  if (accountType === "enterprise") return "/dashboard/enterprise";
  return "/dashboard/admin";
}

type SortKey = keyof Pick<
  Admission,
  "date" | "patient" | "patientType" | "hospital" | "company"
>;

function displayAdmissionTime(time: string | undefined): string {
  if (!time || time === "—") return "";
  return time;
}

export function AdmissionsPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.admissions;
  const isAuditor = getStoredAccountType() === "auditor";
  const isAdmin = getStoredAccountType() === "admin";
  const useApiList = isAuditor || isAdmin;

  const [topSearch, setTopSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const [draftDateFrom, setDraftDateFrom] = useState("");
  const [draftDateTo, setDraftDateTo] = useState("");
  const [draftPatientType, setDraftPatientType] = useState("");
  const [draftHospital, setDraftHospital] = useState("");
  const [draftCompany, setDraftCompany] = useState("");

  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");
  const [appliedPatientType, setAppliedPatientType] = useState("");
  const [appliedHospital, setAppliedHospital] = useState("");
  const [appliedCompany, setAppliedCompany] = useState("");

  const {
    admissions: apiAdmissions,
    loading: apiLoading,
    error: apiError,
    usingApi,
  } = useAuditorAdmissions(
    useApiList
      ? {
          date_start: appliedDateFrom || undefined,
          date_end: appliedDateTo || undefined,
        }
      : {},
  );

  const sourceAdmissions = useApiList && usingApi ? apiAdmissions : mockAdmissions;

  const applyFilters = () => {
    setAppliedDateFrom(draftDateFrom);
    setAppliedDateTo(draftDateTo);
    setAppliedPatientType(draftPatientType);
    setAppliedHospital(draftHospital);
    setAppliedCompany(draftCompany);
    setPage(1);
  };

  const resetFilters = () => {
    setTopSearch("");
    setTableSearch("");
    setDraftDateFrom("");
    setDraftDateTo("");
    setDraftPatientType("");
    setDraftHospital("");
    setDraftCompany("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    setAppliedPatientType("");
    setAppliedHospital("");
    setAppliedCompany("");
    setPage(1);
  };

  const filtered = useMemo(() => {
    const fromDate = parseInputDate(appliedDateFrom);
    const toDate = parseInputDate(appliedDateTo);
    if (toDate) {
      toDate.setHours(23, 59, 59, 999);
    }

    return sourceAdmissions.filter((admission) => {
      const admissionDate = parseAdmissionDate(admission.date, admission.time);
      const haystack =
        `${admission.patient} ${admission.patientSub} ${admission.hospital} ${admission.company}`.toLowerCase();

      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      const byDateFrom = fromDate ? admissionDate >= fromDate : true;
      const byDateTo = toDate ? admissionDate <= toDate : true;
      const byPatientType = appliedPatientType ? admission.patientType === appliedPatientType : true;
      const byHospital = appliedHospital ? admission.hospital === appliedHospital : true;
      const byCompany = appliedCompany ? admission.company === appliedCompany : true;

      return (
        byTopSearch &&
        byTableSearch &&
        byDateFrom &&
        byDateTo &&
        byPatientType &&
        byHospital &&
        byCompany
      );
    });
  }, [
    topSearch,
    tableSearch,
    appliedDateFrom,
    appliedDateTo,
    appliedPatientType,
    appliedHospital,
    appliedCompany,
    sourceAdmissions,
  ]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      let cmp = 0;
      if (sortKey === "date") {
        cmp = parseAdmissionDate(a.date, a.time).getTime() - parseAdmissionDate(b.date, b.time).getTime();
      } else if (sortKey === "patientType") {
        cmp = a.patientType.localeCompare(b.patientType);
      } else {
        cmp = String(a[sortKey]).localeCompare(String(b[sortKey]));
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const handleExportCsv = () => {
    const headers = [
      labels.columns.date,
      labels.columns.patient,
      labels.columns.type,
      labels.columns.hospital,
      labels.columns.company,
    ];
    const rows = [
      headers.join(","),
      ...sorted.map((admission) => {
        const time = displayAdmissionTime(admission.time);
        const dateCell = time ? `${admission.date} ${time}` : admission.date;
        const row = [
          dateCell,
          admission.patient,
          labels.patientTypes[admission.patientType],
          admission.hospital,
          admission.company,
        ];
        return row
          .map((value) => `"${String(value).replaceAll('"', '""')}"`)
          .join(",");
      }),
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "admissions.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const getPatientTypeBadge = (patientType: Admission["patientType"]) => {
    if (patientType === "employee") {
      return (
        <span className="inline-flex items-center rounded-md bg-purple-soft/80 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-purple opacity-90">
          {labels.patientTypes.employee}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-md bg-teal-soft/80 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-teal opacity-90">
        {labels.patientTypes.beneficiary}
      </span>
    );
  };

  const columns: { key: SortKey | "actions"; label: string; sortable?: boolean }[] = [
    { key: "date", label: labels.columns.date, sortable: true },
    { key: "patient", label: labels.columns.patient, sortable: true },
    { key: "patientType", label: labels.columns.type, sortable: true },
    { key: "hospital", label: labels.columns.hospital, sortable: true },
    { key: "company", label: labels.columns.company, sortable: true },
    { key: "actions", label: labels.columns.actions },
  ];

  const displayCount =
    appliedDateFrom ||
    appliedDateTo ||
    appliedPatientType ||
    appliedHospital ||
    appliedCompany ||
    topSearch
      ? filtered.length
      : adminDashboardSummary.admissions;

  return (
    <>
      <Breadcrumbs homeHref={getDashboardHomeHref()} items={[{ label: labels.title }]} />

      <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-foreground">{labels.title}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={topSearch}
            onChange={(event) => {
              setTopSearch(event.target.value);
              setPage(1);
            }}
            placeholder={labels.searchPlaceholder}
            className={`${inputClass} w-full min-w-[200px] max-w-xs`}
          />
          <span className="flex h-8 min-w-8 items-center justify-center rounded-md bg-purple-soft px-2 text-sm font-semibold text-purple">
            {displayCount}
          </span>
          <button
            type="button"
            onClick={handleExportCsv}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
          >
            <Download size={15} />
            {labels.exportCsv}
          </button>
          <button
            type="button"
            onClick={resetFilters}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
            aria-label={t.safecheck.reset}
          >
            <RotateCcw size={15} />
          </button>
        </div>
      </div>

      <FilterCard
        title={t.safecheck.filters}
        onFilter={applyFilters}
        onReset={resetFilters}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField label={labels.fields.dateFrom} hint={labels.fields.dateFromHint}>
            <input
              type="date"
              value={draftDateFrom}
              onChange={(event) => setDraftDateFrom(event.target.value)}
              className={inputClass}
            />
          </FormField>
          <FormField label={labels.fields.dateTo} hint={labels.fields.dateToHint}>
            <input
              type="date"
              value={draftDateTo}
              onChange={(event) => setDraftDateTo(event.target.value)}
              className={inputClass}
            />
          </FormField>
          <FormField label={labels.fields.patientType}>
            <select
              value={draftPatientType}
              onChange={(event) => setDraftPatientType(event.target.value)}
              className={selectClass}
            >
              {admissionPatientTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value ? option.label : labels.fields.allTypes}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.fields.hospital} className="md:col-span-2">
            <select
              value={draftHospital}
              onChange={(event) => setDraftHospital(event.target.value)}
              className={selectClass}
            >
              {admissionHospitalFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value ? option.label : labels.fields.allHospitals}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label={labels.fields.company} className="md:col-span-2">
            <select
              value={draftCompany}
              onChange={(event) => setDraftCompany(event.target.value)}
              className={selectClass}
            >
              {admissionCompanyFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value ? option.label : labels.fields.allCompanies}
                </option>
              ))}
            </select>
          </FormField>
        </div>
      </FilterCard>

      <div className="theme-card">
        <TableToolbar
          search={tableSearch}
          onSearchChange={(value) => {
            setTableSearch(value);
            setPage(1);
          }}
          searchPlaceholder={t.safecheck.search}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={t.safecheck.entries}
        />

        <div className="overflow-x-auto">
          <table className="safecheck-table w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-4 py-3 text-left text-[11px] font-semibold tracking-wide text-muted"
                  >
                    {column.sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(column.key as SortKey)}
                        className="inline-flex items-center gap-1 uppercase"
                      >
                        {column.label}
                        <span className="flex flex-col">
                          <ChevronUp
                            size={10}
                            className={`-mb-1 ${sortKey === column.key && sortDir === "asc" ? "text-primary" : ""}`}
                          />
                          <ChevronDown
                            size={10}
                            className={sortKey === column.key && sortDir === "desc" ? "text-primary" : ""}
                          />
                        </span>
                      </button>
                    ) : (
                      <span className="uppercase">{column.label}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((admission) => (
                <tr
                  key={admission.id}
                  className="border-b border-border last:border-0 hover:bg-card-muted/30"
                >
                  <td className="px-4 py-3 text-sm text-foreground">
                    <Link
                      to={`/dashboard/admissions/${admission.id}`}
                      className="block font-semibold text-primary hover:underline"
                    >
                      {admission.date}
                    </Link>
                    {displayAdmissionTime(admission.time) ? (
                      <p className="text-xs text-muted">{displayAdmissionTime(admission.time)}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground">
                    <p className="font-semibold uppercase">{admission.patient}</p>
                    {admission.patientSub && admission.patientSub !== admission.patient && (
                      <p className="text-xs text-muted">{admission.patientSub}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">{getPatientTypeBadge(admission.patientType)}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{admission.hospital}</td>
                  <td className="px-4 py-3 text-sm text-foreground">{admission.company}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/dashboard/admissions/${admission.id}`}
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-primary/40 bg-primary-soft px-3 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
                    >
                      Voir détails
                    </Link>
                  </td>
                </tr>
              ))}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted">
          <span>
            {labels.pagination.showing} {sorted.length === 0 ? 0 : start + 1}{" "}
            {labels.pagination.to} {Math.min(start + pageSize, sorted.length)}{" "}
            {labels.pagination.of} {sorted.length} {labels.pagination.entries}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.previous}
            </button>
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-lg border border-border px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.next}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
